import { AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { PortalProductsService } from '../../services/portal-products.service';
import { DecimalPipe, NgClass, NgForOf, NgIf } from '@angular/common';
import { Subscription } from 'rxjs';
import { FormsModule } from '@angular/forms';
import { MatPaginatorModule, MatPaginator, PageEvent } from '@angular/material/paginator';

export interface StockAnalysis {
  code: string;
  price: number;
  signal: string;
  color: string;
  volRatio: number;
  pctChange: number;
  high10d: number;
  low10d: number;
  high10w: number;
  low10w: number;
  reasoning: string;
  ai: boolean;
}
export interface Vitals {
  fgScore: number;
  fgText: string;
  momentum: number;
  liquidityPct: number;
}
export interface AiInsight {
  sentiment: string;
  strategy: string;
  stockComments: { [key: string]: string };
}

export interface MarketSummary {
  breakouts: number;
  dips: number;
  weak: number;
  advancers: number;
  decliners: number;
  total: number;
}

export interface DashboardResponse {
  stocks: StockAnalysis[];
  vitals: Vitals;
  ai: AiInsight;
  summary: MarketSummary;
}

@Component({
  selector: 'app-dsc-dashboard',
  standalone: true,
  imports: [DecimalPipe, FormsModule, NgIf, NgClass, NgForOf, MatPaginatorModule],
  templateUrl: './dsc-dashboard.component.html',
  styleUrl: './dsc-dashboard.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DscDashboardComponent implements OnInit, OnDestroy, AfterViewInit {
  private portalProductService = inject(PortalProductsService);
  private cdRef = inject(ChangeDetectorRef);
  data: DashboardResponse | null = null;
  filteredStocks: StockAnalysis[] = [];
  paginatedStocks: StockAnalysis[] = [];
  filterSignal: string = '';
  filterStockCode: string = '';
  loading: boolean = true;
  activeSortColumn = '';
  activeSortOrder = '';

  private refreshSubscription!: Subscription;

  // Pagination properties
  pageSize = 50;
  pageIndex = 0;
  pageSizeOptions = [5, 10, 25, 50];

  @ViewChild(MatPaginator, { static: false }) paginator!: MatPaginator;

  async ngOnInit() {
    await this.loadData();
    // Refresh every 60 seconds automaticallythis.refreshSubscription = interval(60000).subscribe(() => this.loadData());
  }

  ngOnDestroy(): void {
    if (this.refreshSubscription) {
      this.refreshSubscription.unsubscribe();
    }
  }

  ngAfterViewInit(): void {
    // Reset paginator when view is initialized
    if (this.paginator) {
      this.paginator.pageIndex = 0;
    }
  }
  async loadData() {
    // .subscribe({
    // next: (response) => {
    this.data = await this.portalProductService.getDscDashboardData();
    this.filterStocks();
    this.loading = false;
    this.loading = false;
    this.cdRef.detectChanges();
  }

  filterStocks(): void {
    console.log('filterStocks', this.data);
    if (this.data.stocks.length === 0) return;

    let filtered = this.data.stocks;

    // Filter by signal
    if (this.filterSignal) {
      filtered = filtered.filter((s) => s.signal === this.filterSignal);
    }

    // Filter by stock code (case-insensitive)
    if (this.filterStockCode && this.filterStockCode.trim()) {
      const searchTerm = this.filterStockCode.trim().toUpperCase();
      filtered = filtered.filter((s) => s.code.toUpperCase().includes(searchTerm));
    }

    this.filteredStocks = filtered;

    if (this.activeSortColumn && this.activeSortOrder) {
      this.filteredStocks = this.filteredStocks.sort((a, b) => {
        let aVal = a[this.activeSortColumn as keyof StockAnalysis];
        let bVal = b[this.activeSortColumn as keyof StockAnalysis];

        // Handle null/undefined values
        if (aVal == null && bVal == null) return 0;
        if (aVal == null) return 1;
        if (bVal == null) return -1;

        // Handle string comparison (case-insensitive)
        if (typeof aVal === 'string' && typeof bVal === 'string') {
          const comparison = aVal.toLowerCase().localeCompare(bVal.toLowerCase());
          return this.activeSortOrder === 'asc' ? comparison : -comparison;
        }

        // Handle number comparison
        if (typeof aVal === 'number' && typeof bVal === 'number') {
          if (aVal > bVal) return this.activeSortOrder === 'asc' ? 1 : -1;
          if (aVal < bVal) return this.activeSortOrder === 'asc' ? -1 : 1;
          return 0;
        }

        // Fallback comparison
        if (aVal > bVal) return this.activeSortOrder === 'asc' ? 1 : -1;
        if (aVal < bVal) return this.activeSortOrder === 'asc' ? -1 : 1;
        return 0;
      });
    }

    // Reset to first page when filtering
    this.pageIndex = 0;
    if (this.paginator) {
      this.paginator.pageIndex = 0;
    }
    this.updatePaginatedStocks();
    console.log(this.filteredStocks);
    this.cdRef.detectChanges();
  }

  onPageChange(event: PageEvent): void {
    this.pageSize = event.pageSize;
    this.pageIndex = event.pageIndex;
    this.updatePaginatedStocks();
    this.cdRef.detectChanges();
  }

  private updatePaginatedStocks(): void {
    const startIndex = this.pageIndex * this.pageSize;
    const endIndex = startIndex + this.pageSize;
    this.paginatedStocks = this.filteredStocks.slice(startIndex, endIndex);
  }

  // Calculate percentage position for the progress bars
  calculateProgress(price: number, low: number, high: number): number {
    if (low === high) return 50;
    const rng = high - low;
    const pos = price - low;
    const pct = (pos / rng) * 100;
    return Math.max(0, Math.min(100, pct)); // Clamp between 0 and 100
  }

  sortByName(sortColumn: string) {
    // If clicking the same column, toggle between asc/desc
    if (this.activeSortColumn === sortColumn) {
      if (this.activeSortOrder === 'desc') {
        this.activeSortOrder = 'asc';
      } else if (this.activeSortOrder === 'asc') {
        this.activeSortOrder = '';
        this.activeSortColumn = '';
      } else {
        this.activeSortOrder = 'desc';
      }
    } else {
      // New column, start with descending
      this.activeSortColumn = sortColumn;
      this.activeSortOrder = 'desc';
    }
    this.filterStocks();
  }

  getSortIcon(column: string): string {
    if (this.activeSortColumn !== column) {
      return 'fa-sort';
    }
    if (this.activeSortOrder === 'asc') {
      return 'fa-sort-asc';
    }
    if (this.activeSortOrder === 'desc') {
      return 'fa-sort-desc';
    }
    return 'fa-sort';
  }
}
